import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { AdminClientSummary } from '../../clients/index.js'
import type { AdminFacility } from '../../facilities/index.js'
import { Button } from '../../core/index.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'


export const INPUT_CLASS = 'w-full border border-marine/25 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine disabled:bg-canvas disabled:text-ink-mid'
export const LINK_BUTTON = 'text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-60'


/** The button every list page opens its New form with. */
export function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button icon={Plus} onClick={onClick}>
      {label}
    </Button>
  )
}

/** What the lists show when there is nothing in them: a sentence and the one thing to do about it. */
export function EmptyState({ message, actionLabel, onAction }: { message: string; actionLabel: string; onAction: () => void }) {
  return (
    <div className="bg-white border border-marine/15 p-8 text-sm text-ink-mid flex flex-col items-start gap-3">
      <p>{message}</p>
      <NewButton label={actionLabel} onClick={onAction} />
    </div>
  )
}

/** The clients a record can be created under: the active ones, which are the only ones with a clientId. */
export function useActiveClients(api: ClientsAdminApi) {
  const query = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const clients = (query.data ?? []).filter((client) => client.clientId !== null).sort((a, b) => a.organizationName.localeCompare(b.organizationName))
  return { ...query, clients }
}

interface ClientSelectProps {
  id: string
  clients: readonly AdminClientSummary[]
  isLoading: boolean
  value: string
  onChange: (registrationId: string) => void
}

/** Picks the client by registration, which every active client has exactly one of. */
export function ClientSelect({ id, clients, isLoading, value, onChange }: ClientSelectProps) {
  return (
    <select id={id} className={INPUT_CLASS} value={value} onChange={(event) => onChange(event.target.value)} required disabled={isLoading}>
      <option value="">{isLoading ? 'Loading clients…' : 'Choose a client'}</option>
      {clients.map((client) => (
        <option key={client.registrationId} value={client.registrationId}>
          {client.organizationName}
          {client.isDemo ? ' (demo)' : ''} · {client.environment}
        </option>
      ))}
    </select>
  )
}

/** The facilities of one client, on the same cache key the client panel uses. */
export function useClientFacilities(api: FacilitiesAdminApi, clientId: string | null) {
  const query = useQuery({
    queryKey: ['admin', 'facilities', clientId],
    queryFn: () => api.listFacilities(clientId ?? ''),
    enabled: clientId !== null,
  })
  const facilities = [...(query.data ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  return { ...query, facilities }
}

interface FacilitySelectProps {
  id: string
  facilities: readonly AdminFacility[]
  isLoading: boolean
  /** False until a client is chosen. */
  hasClient: boolean
  value: string
  onChange: (facilityId: string) => void
}

export function FacilitySelect({ id, facilities, isLoading, hasClient, value, onChange }: FacilitySelectProps) {
  const placeholder = !hasClient ? 'Choose a client first' : isLoading ? 'Loading facilities…' : facilities.length === 0 ? 'This client has no facilities yet' : 'Choose a facility'
  return (
    <select id={id} className={INPUT_CLASS} value={value} onChange={(event) => onChange(event.target.value)} required disabled={!hasClient || isLoading}>
      <option value="">{placeholder}</option>
      {facilities.map((facility) => (
        <option key={facility.facilityId} value={facility.facilityId}>
          {facility.name}
        </option>
      ))}
    </select>
  )
}
